/**
 * Load status label (ST-00218, lm-status): the per-ticket "Complete / In transit / Needs review /
 * Missing / …" label every ticket list shows, from POST /api/load-matching/ticket-status
 * (server/routes/loadMatching.ts; shapes in `types/loadMatching`).
 *
 *   fetchLoadStatuses(uids)   → { enabled, statuses } for up to any number of uids (batched by
 *                               2000, cached per uid for CACHE_MS; one request in flight per uid)
 *   loadStatusText(s)         → the plain text of a label for exports ("Needs review — Probable
 *                               duplicate weighing"), '' without a status
 *
 * `enabled: false` (LOAD_MATCHING=off on the server) → pages hide the column. A failed lookup
 * resolves as "unknown" (enabled: null) and never breaks the page that asked.
 */
import api from './api';
import type { LoadStatusLookupResponse, TicketLoadStatus } from '../types/loadMatching';

const URL = '/load-matching/ticket-status';
export const LOOKUP_BATCH = 2000;
/** A label read stays fresh this long (the ledger tail runs every 15 min). */
export const CACHE_MS = 2 * 60 * 1000;

export interface LoadStatusResult {
  /** true: show the column; false: LOAD_MATCHING=off (hide it); null: unknown (lookup failed). */
  enabled: boolean | null;
  statuses: Record<string, TicketLoadStatus>;
}

interface CacheEntry {
  at: number;
  status: TicketLoadStatus | null;
}

const cache = new Map<string, CacheEntry>();
let lastEnabled: boolean | null = null;

/** What the last lookup said about the column (true / false), null before any answer. */
export const knownEnabled = (): boolean | null => lastEnabled;

/** Forget every cached label (tests; after an amendment that changes a ticket). */
export function clearLoadStatusCache(uids?: readonly string[]): void {
  if (!uids) {
    cache.clear();
    lastEnabled = null;
    return;
  }
  for (const u of uids) cache.delete(u);
}

async function post(uids: string[], signal?: AbortSignal): Promise<LoadStatusLookupResponse> {
  const res = await api.post(URL, { ticketUids: uids }, { signal, timeout: 60000 });
  return res.data as LoadStatusLookupResponse;
}

/**
 * Labels for `uids` (duplicates and blanks ignored). Fresh cached labels are not asked again;
 * the rest go in batches of LOOKUP_BATCH.
 */
export async function fetchLoadStatuses(
  uids: readonly (string | null | undefined)[],
  { signal, force = false }: { signal?: AbortSignal; force?: boolean } = {}
): Promise<LoadStatusResult> {
  const now = Date.now();
  const list = [...new Set(uids.filter((u): u is string => typeof u === 'string' && !!u))];
  const statuses: Record<string, TicketLoadStatus> = {};
  const ask: string[] = [];
  for (const u of list) {
    const hit = cache.get(u);
    if (!force && hit && now - hit.at < CACHE_MS) {
      if (hit.status) statuses[u] = hit.status;
    } else ask.push(u);
  }
  if (!ask.length) return { enabled: lastEnabled ?? (list.length ? true : null), statuses };
  try {
    let enabled: boolean | null = null;
    for (let i = 0; i < ask.length; i += LOOKUP_BATCH) {
      const chunk = ask.slice(i, i + LOOKUP_BATCH);
      const body = await post(chunk, signal);
      enabled = !!body.enabled;
      lastEnabled = enabled;
      if (!body.enabled) {
        for (const u of ask) cache.delete(u);
        return { enabled: false, statuses: {} };
      }
      const at = Date.now();
      for (const u of chunk) {
        const s = body.statuses?.[u] ?? null;
        cache.set(u, { at, status: s });
        if (s) statuses[u] = s;
      }
    }
    return { enabled, statuses };
  } catch (err) {
    if ((err as { name?: string })?.name === 'CanceledError' || signal?.aborted) throw err;
    return { enabled: lastEnabled === false ? false : null, statuses };
  }
}

/** Plain text of a label for exports and screen readers. */
export function loadStatusText(s: TicketLoadStatus | null | undefined): string {
  if (!s) return '';
  return s.reason ? `${s.label} — ${s.reason}` : s.label;
}
